import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle, XCircle, Copy } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
} from '@/components/ui/Dialog';
import Button from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { getOffer } from '../fixtures/sourceCatalog';
import { IntegrationDemo } from '../demoStore';
import { DataSource, DataTypeKey, PreviewIssue } from '../types';

interface ImportPreviewDialogProps {
  dataType: DataTypeKey | null;
  source: DataSource | undefined;
  demo: IntegrationDemo;
  actorName: string;
  onClose: () => void;
  /** Fired after a successful import so the shell can raise its toast. */
  onImported: () => void;
  /** The "Fix mapping" CTA on a blocked preview. */
  onFixMapping: () => void;
}

const IssueList: React.FC<{ issues: PreviewIssue[]; icon: React.ElementType; tone: string }> = ({
  issues, icon: Icon, tone,
}) => {
  const { t } = useTranslation();
  if (issues.length === 0) return null;
  return (
    <ul className="space-y-1.5">
      {issues.map((issue) => (
        <li key={issue.reasonKey} className={`flex items-start gap-2 text-sm ${tone}`}>
          <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t('extInt.preview.issueLine', {
              count: issue.count,
              reason: t(issue.reasonKey),
              example: issue.exampleLabel,
            })}
          </span>
        </li>
      ))}
    </ul>
  );
};

const ImportPreviewDialog: React.FC<ImportPreviewDialogProps> = ({
  dataType, source, demo, actorName, onClose, onImported, onFixMapping,
}) => {
  const { t } = useTranslation();
  if (!dataType) return null;

  // Nothing can be previewed from a system that does not expose this data type.
  const offer = source ? getOffer(source.system, dataType) : undefined;
  if (!offer) return null;

  // Captured so the confirm handler keeps the narrowed type inside its closure.
  const activeType = dataType;
  const preview = demo.buildPreview(activeType);
  const importing = demo.busyKey === `import:${activeType}`;
  // The signature gate is now enforced, not merely displayed. While it was one of the connection
  // "methods" nothing ever checked it, so a customer who chose approved exchange precisely because
  // their legal team demanded written sign-off could import before anyone signed anything.
  const awaitingSignature = Boolean(source?.requiresSignedExchange && !source?.exchangeSignedOn);
  const blocked = preview.blockingFields.length > 0 || awaitingSignature;
  const total = preview.newCount + preview.updatedCount;

  const handleConfirm = async () => {
    await demo.confirmImport(activeType, actorName);
    onImported();
    onClose();
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => { if (!next && !importing) onClose(); }}
    >
      <DialogContent
        className="max-w-2xl"
        onEscapeKeyDown={(e) => { if (importing) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (importing) e.preventDefault(); }}
        data-testid="preview-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t('extInt.preview.title', { entity: t(`extInt.dataType.${dataType}`) })}
          </DialogTitle>
          <DialogDescription>{t('extInt.preview.description')}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="flex items-start gap-2 text-sm text-foreground">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
            <span data-testid="preview-clean">
              {t('extInt.preview.cleanLine', {
                total,
                created: preview.newCount,
                updated: preview.updatedCount,
              })}
            </span>
          </div>

          <IssueList issues={preview.warnings} icon={AlertTriangle} tone="text-amber-700 dark:text-amber-400" />
          <IssueList issues={preview.skipped} icon={XCircle} tone="text-muted-foreground" />
          <IssueList issues={preview.duplicates} icon={Copy} tone="text-muted-foreground" />

          {preview.blockingFields.length > 0 && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {t('extInt.preview.blocked', { count: preview.blockingFields.length })}
            </p>
          )}

          {awaitingSignature && (
            <p
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
              data-testid="preview-awaiting-signature"
            >
              {t('extInt.preview.awaitingSignature')}
            </p>
          )}

          <p className="text-xs text-muted-foreground">{t('extInt.preview.nothingWritten')}</p>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing} data-testid="preview-cancel">
            {t('extInt.preview.cancel')}
          </Button>
          <Button variant="outline" onClick={onFixMapping} disabled={importing} data-testid="preview-fix-mapping">
            {t('extInt.preview.fixMapping')}
          </Button>
          <Button onClick={() => { void handleConfirm(); }} disabled={blocked || importing} data-testid="preview-confirm">
            {importing && <Spinner size="sm" className="me-2" />}
            {t('extInt.preview.confirm', { count: total })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportPreviewDialog;
